/**
 * Canonical value-slot vocabulary for the cross-calc value bus.
 *
 * Every slot stores a numeric value in its Imperial base unit. Screens
 * convert to/from display units via useUnits().toImp / fromImp when
 * reading or writing the bus. This keeps the bus unit-agnostic across
 * the app's Imperial/SI toggle.
 *
 * Design rule: a slot represents a **physical meaning in the user's
 * workflow**, not just "a number with these units." Two calcs should
 * share a slot only when the value produced by one is the same thing
 * the other expects to consume. When in doubt, split.
 *
 * Add new slots at the bottom as calcs get wired in. Keep names
 * `<quantity>_<unit>` for readability.
 */

export type Slot =
  // ── Airflow / pressure / geometry ─────────────────────────────────────
  | 'velocity_fpm'                  // generic air velocity, duct face, hood face
  | 'velocity_pressure_iwg'
  | 'static_pressure_iwg'
  | 'total_pressure_iwg'
  | 'cfm'                           // generic volumetric airflow
  | 'area_sqft'                     // cross-section area (duct, hood face, filter)
  | 'air_density_lb_ft3'
  | 'duct_diameter_in'
  | 'duct_width_in'
  | 'duct_height_in'
  | 'duct_length_ft'
  | 'friction_loss_iwg_per_100ft'
  | 'equivalent_length_ft'
  | 'k_factor'                      // flow hood K-factor
  | 'leakage_cfm_per_100sqft'       // duct leakage class
  | 'filter_pressure_drop_iwg'

  // ── Temperature & psychrometrics ──────────────────────────────────────
  | 'dry_bulb_f'
  | 'wet_bulb_f'
  | 'dewpoint_f'
  | 'rh_pct'
  | 'humidity_ratio_lb_lb'
  | 'enthalpy_btu_lb'
  | 'specific_volume_ft3_lb'
  | 'outside_air_temp_f'
  | 'return_air_temp_f'
  | 'mixed_air_temp_f'
  | 'outside_air_pct'
  | 'delta_t_f'                     // generic temperature difference

  // ── Heat transfer ─────────────────────────────────────────────────────
  | 'q_btuh'                        // total heat transfer Q
  | 'sensible_heat_btuh'
  | 'latent_heat_btuh'
  | 'shr'                           // sensible heat ratio (0..1)

  // ── Fan / sheave ──────────────────────────────────────────────────────
  | 'fan_rpm'
  | 'motor_rpm'
  | 'fan_pd_in'                     // fan sheave pitch diameter
  | 'motor_pd_in'                   // motor sheave pitch diameter
  | 'belt_length_in'
  | 'center_distance_in'            // sheave centers
  | 'bhp'                           // brake horsepower (fan or motor)
  | 'fan_hp'
  | 'tip_speed_fpm'                 // fan blade tip surface speed — NOT air velocity
  | 'fan_efficiency_pct'

  // ── Hydronic ──────────────────────────────────────────────────────────
  | 'gpm'
  | 'delta_p_psi'
  | 'cv'                            // valve flow coefficient
  | 'specific_gravity'
  | 'water_temp_f'
  | 'pump_head_ft'
  | 'pump_efficiency_pct'
  | 'water_hp'
  | 'boiler_output_btuh'
  | 'boiler_fire_rate_gph'
  | 'boiler_input_btuh'
  | 'boiler_efficiency_pct'
  | 'boiler_hhv'
  | 'boiler_fuel_cost'
  | 'boiler_operating_hours'
  | 'boiler_operating_cost'

  // ── Electrical ────────────────────────────────────────────────────────
  | 'volts'
  | 'amps'
  | 'power_w'
  | 'resistance_ohms'
  | 'power_factor'
  | 'motor_fla'                     // full-load amps
  | 'motor_efficiency_pct'

  // ── Room / enclosure / cleanroom / BET ────────────────────────────────
  | 'volume_cuft'                   // enclosed air volume (room / building)
  | 'floor_area_sqft'               // room floor area
  | 'air_changes_per_hour'
  | 'pressure_differential_pa'
  | 'pressure_differential_iwg'
  | 'particle_concentration_per_ft3'
  | 'sample_volume_ft3'
  | 'scan_rate_fpm'                 // HEPA probe scan rate — NOT air velocity
  | 'iso_class'
  | 'effective_leakage_area_sqin';

export const SLOT_META: Record<Slot, { label: string; unitImp: string; qty: string }> = {
  velocity_fpm:                  { label: 'Velocity',              unitImp: 'FPM',         qty: 'velocity' },
  velocity_pressure_iwg:         { label: 'Velocity Pressure',     unitImp: 'in. w.g.',    qty: 'pressure' },
  static_pressure_iwg:           { label: 'Static Pressure',       unitImp: 'in. w.g.',    qty: 'pressure' },
  total_pressure_iwg:            { label: 'Total Pressure',        unitImp: 'in. w.g.',    qty: 'pressure' },
  cfm:                           { label: 'Flow Rate',             unitImp: 'CFM',         qty: 'flow' },
  area_sqft:                     { label: 'Area',                  unitImp: 'ft²',         qty: 'area' },
  air_density_lb_ft3:            { label: 'Air Density',           unitImp: 'lb/ft³',      qty: 'density' },
  duct_diameter_in:              { label: 'Duct Diameter',         unitImp: 'in',          qty: 'diameter' },
  duct_width_in:                 { label: 'Duct Width',            unitImp: 'in',          qty: 'diameter' },
  duct_height_in:                { label: 'Duct Height',           unitImp: 'in',          qty: 'diameter' },
  duct_length_ft:                { label: 'Duct Length',           unitImp: 'ft',          qty: 'length' },
  friction_loss_iwg_per_100ft:   { label: 'Friction Loss',         unitImp: 'in. w.g./100 ft', qty: 'friction' },
  equivalent_length_ft:          { label: 'Equivalent Length',     unitImp: 'ft',          qty: 'length' },
  k_factor:                      { label: 'K-factor',              unitImp: '',            qty: 'dimensionless' },
  leakage_cfm_per_100sqft:       { label: 'Leakage Rate',          unitImp: 'CFM/100 ft²', qty: 'leakage' },
  filter_pressure_drop_iwg:      { label: 'Filter ΔP',             unitImp: 'in. w.g.',    qty: 'pressure' },

  dry_bulb_f:                    { label: 'Dry Bulb',              unitImp: '°F',          qty: 'temperature' },
  wet_bulb_f:                    { label: 'Wet Bulb',              unitImp: '°F',          qty: 'temperature' },
  dewpoint_f:                    { label: 'Dew Point',             unitImp: '°F',          qty: 'temperature' },
  rh_pct:                        { label: 'Relative Humidity',     unitImp: '%',           qty: 'percent' },
  humidity_ratio_lb_lb:          { label: 'Humidity Ratio',        unitImp: 'lb/lb',       qty: 'humidity_ratio' },
  enthalpy_btu_lb:               { label: 'Enthalpy',              unitImp: 'Btu/lb',      qty: 'enthalpy' },
  specific_volume_ft3_lb:        { label: 'Specific Volume',       unitImp: 'ft³/lb',      qty: 'specific_volume' },
  outside_air_temp_f:            { label: 'Outside Air Temp',      unitImp: '°F',          qty: 'temperature' },
  return_air_temp_f:             { label: 'Return Air Temp',       unitImp: '°F',          qty: 'temperature' },
  mixed_air_temp_f:              { label: 'Mixed Air Temp',        unitImp: '°F',          qty: 'temperature' },
  outside_air_pct:               { label: 'Outside Air %',         unitImp: '%',           qty: 'percent' },
  delta_t_f:                     { label: 'ΔT',                    unitImp: '°F',          qty: 'temperature' },

  q_btuh:                        { label: 'Heat Transfer Q',       unitImp: 'Btu/h',       qty: 'heat' },
  sensible_heat_btuh:            { label: 'Sensible Heat',         unitImp: 'Btu/h',       qty: 'heat' },
  latent_heat_btuh:              { label: 'Latent Heat',           unitImp: 'Btu/h',       qty: 'heat' },
  shr:                           { label: 'Sensible Heat Ratio',   unitImp: '',            qty: 'dimensionless' },

  fan_rpm:                       { label: 'Fan RPM',               unitImp: 'RPM',         qty: 'rpm' },
  motor_rpm:                     { label: 'Motor RPM',             unitImp: 'RPM',         qty: 'rpm' },
  fan_pd_in:                     { label: 'Fan Pitch Dia.',        unitImp: 'in',          qty: 'diameter' },
  motor_pd_in:                   { label: 'Motor Pitch Dia.',      unitImp: 'in',          qty: 'diameter' },
  belt_length_in:                { label: 'Belt Length',           unitImp: 'in',          qty: 'diameter' },
  center_distance_in:            { label: 'Center Distance',       unitImp: 'in',          qty: 'diameter' },
  bhp:                           { label: 'Brake Horsepower',      unitImp: 'HP',          qty: 'horsepower' },
  fan_hp:                        { label: 'Fan Horsepower',        unitImp: 'HP',          qty: 'horsepower' },
  tip_speed_fpm:                 { label: 'Tip Speed',             unitImp: 'FPM',         qty: 'velocity' },
  fan_efficiency_pct:            { label: 'Fan Efficiency',        unitImp: '%',           qty: 'percent' },

  gpm:                           { label: 'Flow Rate',             unitImp: 'GPM',         qty: 'flow_gpm' },
  delta_p_psi:                   { label: 'Pressure Drop',         unitImp: 'psi',         qty: 'pressure_psi' },
  cv:                            { label: 'Cv',                    unitImp: '',            qty: 'dimensionless' },
  specific_gravity:              { label: 'Specific Gravity',      unitImp: '',            qty: 'dimensionless' },
  water_temp_f:                  { label: 'Water Temp',            unitImp: '°F',          qty: 'temperature' },
  pump_head_ft:                  { label: 'Pump Head',             unitImp: 'ft',          qty: 'head' },
  pump_efficiency_pct:           { label: 'Pump Efficiency',       unitImp: '%',           qty: 'percent' },
  water_hp:                      { label: 'Water Horsepower',      unitImp: 'HP',          qty: 'horsepower' },
  boiler_output_btuh:            { label: 'Boiler Output',         unitImp: 'Btu/h',       qty: 'heat' },
  boiler_fire_rate_gph:          { label: 'Boiler Fire Rate',      unitImp: 'GPH',         qty: 'flow_gpm' },
  boiler_input_btuh:             { label: 'Boiler Input',          unitImp: 'Btu/h',       qty: 'heat' },
  boiler_efficiency_pct:         { label: 'Boiler Efficiency',     unitImp: '%',           qty: 'percent' },
  boiler_hhv:                    { label: 'HHV',                   unitImp: 'BTU/lb',      qty: 'dimensionless' },
  boiler_fuel_cost:              { label: 'Fuel Cost',             unitImp: '$/unit',      qty: 'dimensionless' },
  boiler_operating_hours:        { label: 'Operating Hours',       unitImp: 'h',           qty: 'dimensionless' },
  boiler_operating_cost:         { label: 'Operating Cost',        unitImp: '$',           qty: 'dimensionless' },

  volts:                         { label: 'Voltage',               unitImp: 'V',           qty: 'volts' },
  amps:                          { label: 'Current',               unitImp: 'A',           qty: 'amps' },
  power_w:                       { label: 'Power',                 unitImp: 'W',           qty: 'power' },
  resistance_ohms:               { label: 'Resistance',            unitImp: 'Ω',           qty: 'resistance' },
  power_factor:                  { label: 'Power Factor',          unitImp: '',            qty: 'dimensionless' },
  motor_fla:                     { label: 'Motor FLA',             unitImp: 'A',           qty: 'amps' },
  motor_efficiency_pct:          { label: 'Motor Efficiency',      unitImp: '%',           qty: 'percent' },

  volume_cuft:                   { label: 'Volume',                unitImp: 'ft³',         qty: 'volume' },
  floor_area_sqft:               { label: 'Floor Area',            unitImp: 'ft²',         qty: 'area' },
  air_changes_per_hour:          { label: 'Air Changes/hr',        unitImp: 'ACH',         qty: 'ach' },
  pressure_differential_pa:      { label: 'Pressure Differential', unitImp: 'Pa',          qty: 'pressure_pa' },
  pressure_differential_iwg:     { label: 'Pressure Differential', unitImp: 'in. w.g.',    qty: 'pressure' },
  particle_concentration_per_ft3:{ label: 'Particle Concentration',unitImp: 'particles/ft³', qty: 'concentration' },
  sample_volume_ft3:             { label: 'Sample Volume',         unitImp: 'ft³',         qty: 'volume' },
  scan_rate_fpm:                 { label: 'Scan Rate',             unitImp: 'FPM',         qty: 'velocity' },
  iso_class:                     { label: 'ISO Class',             unitImp: '',            qty: 'dimensionless' },
  effective_leakage_area_sqin:   { label: 'Effective Leakage Area',unitImp: 'in²',         qty: 'area' },
};
